import { lazy } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import ModuleRouteGuard from "../common/components/ModuleRouteGuard";
import AffiliateRouteGuard from "../modules/Admin/Affiliate/components/AffiliateRouteGuard";
import SettingsRouteGuard from "../modules/Admin/Settings/components/SettingsRouteGuard";
import MainLayout from "./layouts/main-layout";
import PrivateRoute from "./private-route";

const AdminMainPage = lazy(() => import("../modules/Admin"));
const Affiliate = lazy(() => import("../modules/Admin/Affiliate"));
const AffiliatesPage = lazy(
  () => import("../modules/Admin/Affiliate/ProvidersAndGroup"),
);
const TeladocHealthAffiliate = lazy(
  () => import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health"),
);
const BillsPage = lazy(
  () =>
    import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health/Bills"),
);
const LinkedProvidersLocations = lazy(
  () =>
    import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health/LinkedProvidersAndLocations"),
);
const LocationsPage = lazy(
  () =>
    import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health/LinkedProvidersAndLocations/Locations"),
);
const ProvidersPage = lazy(
  () =>
    import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health/LinkedProvidersAndLocations/Providers"),
);
const PatientsPage = lazy(
  () =>
    import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health/Patients"),
);
const AffiliateSettings = lazy(
  () => import("../modules/Admin/Affiliate/Settings"),
);
const AffiliateSettingsNotificationPreference = lazy(
  () => import("../modules/Admin/Affiliate/Settings/NotificationPreference"),
);
const AffiliateSettingsProfile = lazy(
  () => import("../modules/Admin/Affiliate/Settings/Profile"),
);
const AffiliateSettingsProviders = lazy(
  () => import("../modules/Admin/Affiliate/Settings/Providers"),
);
const AffiliateSettingsTerms = lazy(
  () => import("../modules/Admin/Affiliate/Settings/TermsAndCondition/index"),
);
const AffiliateDashboard = lazy(
  () => import("../modules/Admin/Affiliate/Dashboard"),
);
const AffiliateMaps = lazy(() => import("../modules/Admin/Maps"));
const Membership = lazy(() => import("../modules/Admin/Membership"));
const AdminTimeline = lazy(() => import("../modules/Admin/Settings/AuditLog"));
const GlobalHolidays = lazy(
  () => import("../modules/Admin/Settings/GlobalHolidays"),
);
const CancellationReasons = lazy(
  () => import("../modules/Admin/Membership/cancellation-reasons"),
);
const Coupons = lazy(() => import("../modules/Admin/Membership/Coupons"));
const Invoicing = lazy(() => import("../modules/Admin/Membership/Invoicing"));
const InvoiceTemplateView = lazy(
  () => import("../modules/Admin/Membership/Invoicing/TemplateView"),
);
const Plans = lazy(() => import("../modules/Admin/Membership/Plans"));
const Plandetails = lazy(
  () =>
    import("../modules/Admin/Membership/Plans/forms/PlanDetails/Plandetails"),
);
const Settings = lazy(() => import("../modules/Admin/Settings"));
const Account = lazy(() => import("../modules/Admin/Settings/Account"));
const Billing = lazy(() => import("../modules/Admin/Settings/Account/Billing"));
const General = lazy(() => import("../modules/Admin/Settings/Account/General"));
const PaymentSources = lazy(
  () => import("../modules/Admin/Settings/Account/PaymentSources"),
);
const Users = lazy(() => import("../modules/Admin/Settings/Account/Users"));
const ChargeAndTaxes = lazy(
  () => import("../modules/Admin/Settings/ChargeAndTaxes"),
);
const ChargeCategories = lazy(
  () => import("../modules/Admin/Settings/ChargeAndTaxes/charge-categories"),
);
const ChargeItems = lazy(
  () => import("../modules/Admin/Settings/ChargeAndTaxes/charge-items"),
);
const SalesTax = lazy(
  () => import("../modules/Admin/Settings/ChargeAndTaxes/sales-tax"),
);
const Notifications = lazy(
  () => import("../modules/Admin/Settings/Notifications"),
);
const MembershipNotification = lazy(
  () => import("../modules/Admin/Settings/Notifications/Membership"),
);
const MembershipTemplateView = lazy(
  () =>
    import("../modules/Admin/Settings/Notifications/Membership/TemplateView"),
);
const PatientNotification = lazy(
  () => import("../modules/Admin/Settings/Notifications/Patient"),
);
const TemplateView = lazy(
  () => import("../modules/Admin/Settings/Notifications/Patient/TemplateView"),
);
const PhpAdminNotification = lazy(
  () => import("../modules/Admin/Settings/Notifications/PhpAdmin"),
);
const PracticeNotification = lazy(
  () => import("../modules/Admin/Settings/Notifications/Practice"),
);
const NotificationSettings = lazy(
  () => import("../modules/Admin/Settings/Notifications/Settings"),
);
const ProvidersAndPatients = lazy(
  () => import("../modules/Admin/Settings/ProvidersAndPatients"),
);
const LocationGroups = lazy(
  () => import("../modules/Admin/Settings/ProvidersAndPatients/LocationGroups"),
);
const Locations = lazy(
  () => import("../modules/Admin/Settings/ProvidersAndPatients/Locations"),
);
const NurseStaff = lazy(
  () => import("../modules/Admin/Settings/ProvidersAndPatients/NurseStaff"),
);
const Providers = lazy(
  () => import("../modules/Admin/Settings/ProvidersAndPatients/Providers"),
);
const RolesAndPermissions = lazy(
  () => import("../modules/Admin/Settings/RolesAndPermissions"),
);
const AffiliateWrapper = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/affiliateWrapper"),
);
const EmployeeBasicDetails = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/BasicsDetails"),
);
const EmployeeCredits = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Credits"),
);
const EmployeeInvoices = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Invoices"),
);
const EmployeeMemberships = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Memberships"),
);
const EmployeeNotes = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Notes"),
);
const EmployeeNotifications = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Notifications"),
);
const EmployeePaymentOptions = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/PaymentOptions"),
);
const FormBuilderMain = lazy(() => import("../modules/Admin/FormBuilder"));
const FormBuilder = lazy(
  () => import("../modules/Admin/FormBuilder/FormBuilder"),
);
const AffiliateInvoiceLayout = lazy(
  () => import("../common/components/InvoiceLayout/AffiliateInvoiceLayout"),
);
const MembershipDetails = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Memberships/MembershipDetails"),
);
const AffiliateUsers = lazy(
  () =>
    import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health/AffiliateUsers"),
);
const Agreements = lazy(
  () =>
    import("../modules/Admin/Affiliate/ProvidersAndGroup/Teledoc_health/ChargeCategories"),
);
// import AffiliateSettingsProfile from "../modules/Admin/Affiliate/Settings/tabs/Profile";

const MainRoute = () => (
  <PrivateRoute>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </PrivateRoute>
);

const AdminRoutes = (
  <Route path="/admin" element={<MainRoute />}>
    <Route
      path="dashboard"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="dashboard">
          <AdminMainPage />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="users"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="users">
          <div>Admin Users Management</div>
        </ModuleRouteGuard>
      }
    />
    <Route
      path="form-builder"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="form-builder">
          <FormBuilderMain />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="form-builder/add"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="form-builder">
          <FormBuilder mode="create" />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="form-builder/edit/:uuid"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="form-builder">
          <FormBuilder mode="edit" />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="reports"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="reports">
          <div>Admin Reports</div>
        </ModuleRouteGuard>
      }
    />
    <Route
      path="education-materials"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="education-materials">
          <div>Admin Education Materials</div>
        </ModuleRouteGuard>
      }
    />
    <Route
      path="membership"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="membership">
          <Membership />
        </ModuleRouteGuard>
      }
    >
      <Route
        path="plans"
        element={
          <ModuleRouteGuard
            moduleType="admin"
            routePath="Admin Membership Plan"
          >
            <Plans />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="coupons/"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="coupons">
            <Coupons />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="cancellation-reasons/"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="cancellation-reason">
            <CancellationReasons />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="invoicing"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="invoicing-ahead">
            <Invoicing />
          </ModuleRouteGuard>
        }
      />

      <Route
        path="invoicing-template/:uuid/customize"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="invoicing-ahead">
            <InvoiceTemplateView />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="maps/global"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="maps">
          <AffiliateMaps />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="membership/plans/details/:id"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="Admin Membership Plan">
          <Plandetails />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="affiliate"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="affiliate">
          <Affiliate />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route
        path="providers-and-group"
        element={
          <AffiliateRouteGuard routeType="providers-and-group">
            <AffiliatesPage />
          </AffiliateRouteGuard>
        }
      />
      <Route
        path="charge-categories"
        element={
          <AffiliateRouteGuard routeType="charge-categories">
            <ChargeCategories />
          </AffiliateRouteGuard>
        }
      />
      <Route
        path="dashboard"
        element={
          <AffiliateRouteGuard routeType="dashboard">
            <AffiliateDashboard />
          </AffiliateRouteGuard>
        }
      />
      <Route
        path="settings"
        element={
          <AffiliateRouteGuard routeType="settings">
            <AffiliateSettings />
          </AffiliateRouteGuard>
        }
      >
        <Route index element={<Navigate to="providers" replace />} />
        <Route
          path="providers"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="providers">
              <AffiliateSettingsProviders />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="notification-preference"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="notifications">
              <AffiliateSettingsNotificationPreference />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="terms-and-conditions"
          element={
            <ModuleRouteGuard
              moduleType="admin"
              routePath="terms-and-conditions"
            >
              <AffiliateSettingsTerms />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="profile"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="settings">
              <AffiliateSettingsProfile />
            </ModuleRouteGuard>
          }
        />

        <Route path="*" element={<Navigate to="providers" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="providers-and-group" replace />} />
    </Route>

    <Route
      path="affiliate/teladoc-health/:affiliateUuid"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="affiliate">
          <TeladocHealthAffiliate />
        </ModuleRouteGuard>
      }
    >
      <Route index element={<Navigate to="bills" replace />} />
      <Route
        path="bills"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="affiliate">
            <BillsPage />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="patients"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="affiliate">
            <PatientsPage />
          </ModuleRouteGuard>
        }
      />
      <Route path="patients/:employeeUuid" element={<AffiliateWrapper />}>
        <Route index element={<Navigate to="employee-details" replace />} />
        <Route
          path="employee-details"
          element={<EmployeeBasicDetails feature="Admin Affiliate Patients" />}
        />
      </Route>
      <Route
        path="linked-providers-and-locations"
        element={<LinkedProvidersLocations />}
      >
        <Route index element={<Navigate to="providers" replace />} />
        <Route
          path="providers"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="providers">
              <ProvidersPage />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="locations"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="locations">
              <LocationsPage />
            </ModuleRouteGuard>
          }
        />
        <Route path="*" element={<Navigate to="providers" replace />} />
      </Route>
      <Route
        path="charge-categories"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="affiliate">
            <Agreements />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="affiliate-users"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="affiliate">
            <AffiliateUsers />
          </ModuleRouteGuard>
        }
      />
      <Route path="*" element={<Navigate to="bills" replace />} />
    </Route>
    <Route
      path="affiliate/teladoc-health/:affiliateUuid/bills/:employeeUuid/membership-details"
      element={
        // <ModuleRouteGuard moduleType="admin" routePath="memberships">
        <MembershipDetails feature="Billing Memberships" />
        // </ModuleRouteGuard>
      }
    />

    <Route
      path="affiliate/teladoc-health/:affiliateUuid/bills/:invoiceUuid/:mode?"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="invoices">
          <AffiliateInvoiceLayout />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="settings"
      element={
        <ModuleRouteGuard moduleType="admin" routePath="settings">
          <Settings />
        </ModuleRouteGuard>
      }
    >
      <Route
        path="account/"
        element={
          <SettingsRouteGuard routeType="account">
            <Account />
          </SettingsRouteGuard>
        }
      >
        <Route index element={<Navigate to="users" replace />} />
        <Route
          path="general"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="settings">
              <General />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="users"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="users">
              <Users />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="billing"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="settings">
              <Billing />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="payment-sources"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="payment-sources">
              <PaymentSources />
            </ModuleRouteGuard>
          }
        />
        <Route path="*" element={<Navigate to="users" replace />} />
      </Route>
      <Route
        path="providers-and-patients"
        element={
          <SettingsRouteGuard routeType="providers-and-patients">
            <ProvidersAndPatients />
          </SettingsRouteGuard>
        }
      >
        <Route index element={<Navigate to="providers" replace />} />
        <Route
          path="providers"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="providers">
              <Providers />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="nurse-staff"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="providers">
              <NurseStaff />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="locations"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="locations">
              <Locations />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="location-groups"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="location-groups">
              <LocationGroups />
            </ModuleRouteGuard>
          }
        />
        <Route path="*" element={<Navigate to="providers" replace />} />
      </Route>
      <Route
        path="charge-and-taxes/"
        element={
          <SettingsRouteGuard routeType="charge-and-taxes">
            <ChargeAndTaxes />
          </SettingsRouteGuard>
        }
      >
        <Route index element={<Navigate to="charge-items" replace />} />
        <Route
          path="charge-items"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="charge-and-taxes">
              <ChargeItems />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="charge-categories"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="charge-and-taxes">
              <ChargeCategories />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="sales-tax"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="charge-and-taxes">
              <SalesTax />
            </ModuleRouteGuard>
          }
        />
        <Route path="*" element={<Navigate to="charge-items" replace />} />
      </Route>
      <Route
        path="roles-and-permissions"
        element={
          <SettingsRouteGuard routeType="roles-and-permissions">
            <RolesAndPermissions />
          </SettingsRouteGuard>
        }
      />
      <Route
        path="notifications/"
        element={
          <SettingsRouteGuard routeType="notifications">
            <Notifications />
          </SettingsRouteGuard>
        }
      >
        <Route index element={<Navigate to="patient" replace />} />
        <Route
          path="patient"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="notifications">
              <PatientNotification />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="membership"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="notifications">
              <MembershipNotification />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="php-admin"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="notifications">
              <PhpAdminNotification />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="practice"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="notifications">
              <PracticeNotification />
            </ModuleRouteGuard>
          }
        />
        <Route
          path="settings"
          element={
            <ModuleRouteGuard moduleType="admin" routePath="notifications">
              <NotificationSettings />
            </ModuleRouteGuard>
          }
        />
        <Route path="*" element={<Navigate to="patient" replace />} />
      </Route>
      <Route
        path="notifications-template/:uuid"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="notifications">
            <TemplateView />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notifications-template/:uuid/customize"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="notifications">
            <TemplateView />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notifications-template/membership/:uuid"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="notifications">
            <MembershipTemplateView />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notifications-template/membership/:uuid/customize"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="notifications">
            <MembershipTemplateView />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="global-holidays"
        element={
          <SettingsRouteGuard routeType="global-holidays">
            <ModuleRouteGuard moduleType="admin" routePath="global-holidays">
              <GlobalHolidays />
            </ModuleRouteGuard>
          </SettingsRouteGuard>
        }
      />
      <Route
        path="audit-log"
        element={
          <ModuleRouteGuard moduleType="admin" routePath="audit-log">
            <AdminTimeline />
          </ModuleRouteGuard>
        }
      />
    </Route>
  </Route>
);

export default AdminRoutes;
